import { Fragment, useMemo, useState } from 'react';
import { useApp } from '../App.jsx';
import { Card, ConfirmDialog, Field, Icon, Money, MonthSelect, PersonDot, Toggle, MoneyField, useMoneyText } from './ui.jsx';
import { SalarySummary, SalaryDialog, PersonDialog, normalMonth } from './salary.jsx';
import { monthLabel, monthName, paydayRuleLabel, dateLabel } from '../lib/calendar.js';
import { fmt, r2 } from '../lib/money.js';
import { jovemProjection, compareFirstYear, offerComparison } from '../lib/projection.js';
import { taxParamsFor, irsJovemCap, specificDeductionAmount } from '../lib/tax/params.js';
import { updatePerson, deletePlan, addPerson, removePerson } from '../lib/actions.js';
import { versionAt } from '../lib/state.js';
import { withPlanDefaults } from '../lib/payslip.js';

/* ── What reaches the bank, in sentences ───────────────── */

function TakeHome({ person, proj }) {
  const { data, model } = useApp();
  const plan = versionAt(person.plans, model.current) || person.plans[0];
  const ref = useMemo(() => normalMonth(plan, person, data.taxYears, plan.from > model.current ? plan.from : model.current), [plan, person, data.taxYears, model.current]);
  if (!ref) return null;
  const year = Number(ref.month.slice(0, 4));
  const row = proj.rows.find((r) => r.year === year);
  const slip = ref.slip;
  const deductions = slip.lines.filter((l) => l.key === 'deduction');
  const bonus = row?.bonusMonths || [];
  const j = ref.jovem;
  const later = ref.month > model.current;
  const firstJobYear = Number((person.plans[0].startDate || person.plans[0].from || '').slice(0, 4));
  const p = withPlanDefaults(plan);
  const mealTypical = p.mealType === 'none' ? 0 : r2((p.mealPerDay || 0) * 21);
  return (
    <Card title="What reaches your account">
      <div className="take-home">
        <div className="tile hero" style={{ border: 0, padding: 0, background: 'none' }}>
          <div className="label">In a normal month{later ? `, from ${monthLabel(ref.month)}` : ''}, to your bank</div>
          <div className="value num">{fmt(ref.net)}</div>
          {mealTypical > 0 && <div className="sub" style={{ fontSize: 13 }}>+ <Money value={mealTypical} /> on the meal card = <b className="num" style={{ color: 'var(--text)' }}><Money value={ref.net + mealTypical} /></b></div>}
        </div>
        <ul className="plain-list">
          {bonus.length > 0 && (
            <li>
              {bonus.length === 2 && bonus[0].extra === bonus[1].extra
                ? <>In <b>{monthName(bonus[0].month)}</b> and <b>{monthName(bonus[1].month)}</b> you get <b><Money value={bonus[0].extra} sign /></b> more (holiday and Christmas pay).</>
                : bonus.map((b) => <span key={b.month}>In <b>{monthName(b.month)} {year}</b> you get <b><Money value={b.extra} sign /></b> more ({b.month === withPlanDefaults(plan).subsidies.holidayMonth ? 'holiday pay' : 'Christmas pay'}{firstJobYear === year ? ', for the part of the year you worked' : ''}). </span>)}
            </li>
          )}
          {slip.mealCard > 0 && <li>The meal card gets {fmt(p.mealPerDay)} for each working day (about <b><Money value={mealTypical} /></b> a month){data.settings.countMealCard === false ? '; it isn\'t counted in savings' : ''}.</li>}
          {plan.allowance > 0 && plan.allowanceType === 'reimbursement' && <li>Expenses you claim (up to <Money value={plan.allowance} /> a month) come back on top. They aren't counted, because they only return money you already spent.</li>}
          <li>
            {j.eligible && j.pct === 100 && slip.irs === 0 && <>No IRS is taken in {year}: it's year 1 of IRS Jovem, so all your salary is tax-free.</>}
            {j.eligible && j.pct < 100 && <>IRS takes <b><Money value={slip.irs} /></b>: it's year {j.index} of IRS Jovem, so {j.pct}% of your salary is tax-free.</>}
            {!j.eligible && <>IRS takes <b><Money value={slip.irs} /></b>{slip.irs ? '' : ' (nothing at this salary)'}.</>}
          </li>
          {slip.ss > 0 && <li>Social security takes <b><Money value={slip.ss} /></b> ({withPlanDefaults(plan).ssRate}% of your {withPlanDefaults(plan).commissionSS === false ? 'base salary' : 'pay'}).</li>}
          {deductions.map((d) => <li key={d.label}>{d.label} takes <b><Money value={-d.amount} /></b>.</li>)}
          {row?.settlement && Math.abs(row.settlement.refund) >= 1 && (
            <li>After filing your {year} IRS, you should get about <b><Money value={row.settlement.refund} /></b> back in {year + 1}.</li>
          )}
        </ul>
      </div>
    </Card>
  );
}

/* ── The offer letter next to IRS Jovem ─────────────────── */

function OfferComparison({ person }) {
  const { data, model } = useApp();
  const cmp = useMemo(() => offerComparison(data, person.id, model.current), [data, person.id, model.current]);
  if (!cmp) return null;
  const cols = cmp.columns;
  const first = cols[0];
  const fromLetter = cmp.source === 'letter';
  const cell = (c, v, { neg = false, sign = false } = {}) => (
    <td className={c.letter ? 'letter-col' : ''}>{v === null || v === undefined ? '—' : <Money value={neg && v ? -v : v} sign={sign} color={sign} />}</td>
  );
  const row = (label, get, opts = {}) => (
    <tr className={opts.strong ? 'strong-row' : ''}>
      <td className="left">{label}{opts.note && <span className="exp">{opts.note}</span>}</td>
      {cols.map((c, i) => <Fragment key={i}>{cell(c, typeof get === 'function' ? get(c) : c[get], opts)}</Fragment>)}
    </tr>
  );
  const hasCommissionIrs = cols.some((c) => c.irsCommission !== null && c.irsCommission !== undefined);
  return (
    <Card title={fromLetter ? 'Your offer letter, with IRS Jovem' : 'With and without IRS Jovem'}>
      <p className="dim" style={{ marginBottom: 10 }}>
        {fromLetter
          ? "Your offer letter's lines, with IRS Jovem applied. Only the IRS lines change: what's left of them is the part of your income still taxed (none in the first year, then 25%, 50% and 75%)."
          : 'A normal month and a year of this salary, without IRS Jovem and in each IRS Jovem stage.'}
      </p>
      <div className="table-wrap">
        <table className="sheet compare">
          <thead>
            <tr>
              <th className="left" />
              {cols.map((c, i) => <th key={i} className={c.letter ? 'letter-col' : ''}><div>{c.label}</div><div className="th-sub">{c.sub}</div></th>)}
            </tr>
          </thead>
          <tbody>
            {row(fromLetter ? 'IRS (×14)' : 'IRS', 'irsBase', { neg: true })}
            {row('Seg Social (×14)', 'ss', { neg: true })}
            {hasCommissionIrs && row('IRS Variável (×12)', 'irsCommission', { neg: true, note: 'on commission' })}
            {cmp.deductionLabels.map((l, j) => <Fragment key={l}>{row(`${l} (×12)`, (c) => c.deductions[j], { neg: true })}</Fragment>)}
            {row('Total Imposto', 'total', { neg: true, strong: true })}
            {row('Vencimento Líquido Anual', 'annual', { note: 'net a year' })}
            {row('Vencimento Líquido Mensal', 'mensal', { note: 'a year ÷ 12' })}
            {row('Vencimento Líquido Mensal s/Subsídio', 'ssub', { strong: true, note: 'a normal month, lunch allowance included' })}
            {row(fromLetter ? 'More than the letter, a month' : 'More than without IRS Jovem', (c) => (c.letter ? null : r2(c.ssub - first.ssub)), { sign: true })}
            {cmp.allowance > 0 && row('Plano Salarial Líquido Anual', 'planoAnual', { note: `with the ${fmt(cmp.allowance)} Despesas` })}
            {cmp.allowance > 0 && row('Plano Salarial Líquido Mensal', 'planoMensal')}
            {cmp.allowance > 0 && row('Plano Salarial Líquido Mensal s/Subsídio', 'planoSsub')}
            {row('To your bank in a normal month', 'bank', { note: 'without the lunch allowance' })}
          </tbody>
        </table>
      </div>
      <p className="small muted" style={{ marginTop: 8 }}>
        {fromLetter && 'From 2027 your payslips use the official IRS tables, which take a little more than the letter estimates (see “Year by year”); the yearly IRS refund evens it out. '}
        The Despesas only come when you claim expenses, and the yearly IRS refund isn't included.
      </p>
    </Card>
  );
}

/* ── IRS Jovem, one line per year ──────────────────────── */

function JovemYears({ proj }) {
  const rows = proj.rows.filter((r) => r.normal);
  if (!rows.length) return null;
  const bonusText = (r) => {
    if (!r.bonusMonths.length) return '—';
    if (r.bonusMonths.length === 2 && r.bonusMonths[0].extra === r.bonusMonths[1].extra) return <><Money value={r.bonusMonths[0].extra} sign /> each</>;
    return r.bonusMonths.map((b) => <div key={b.month}>{monthName(b.month).slice(0, 3)} <Money value={b.extra} sign /></div>);
  };
  return (
    <Card title="Year by year">
      <p className="dim" style={{ marginBottom: 10 }}>What your payslips should bring, worked out with the official IRS tables (what payroll uses). IRS Jovem makes part of your salary tax-free for your first 10 years of work, while you're 35 or younger; the tax-free part goes down over time. Same salary assumed.</p>
      <div className="table-wrap">
        <table className="sheet">
          <thead><tr><th className="left">Year</th><th className="left">Tax-free</th><th>Normal month</th><th>June & December</th><th className="total">IRS back the next year</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.year} className={r.jovem.eligible ? '' : 'future'}>
                <td className="left">{r.year}</td>
                <td className="left">{r.jovem.eligible ? <span className="badge partial">{r.jovem.pct}%</span> : <span className="small muted">none</span>}</td>
                <td><Money value={r.normal.net} /></td>
                <td>{bonusText(r)}</td>
                <td className="total">{r.settlement ? <Money value={r.settlement.refund} color /> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.some((r) => r.estimatedTax) && <p className="small muted" style={{ marginTop: 8 }}>Years after {rows.find((r) => !r.estimatedTax)?.year ?? rows[0].year} use the latest tax rules the app knows, so they're estimates.</p>}
      {rows.some((r) => r.jovem.ageUnknown) && <p className="small" style={{ marginTop: 6, color: 'var(--warn)' }}>Add the birth date (About) — IRS Jovem stops the year you turn 36.</p>}
    </Card>
  );
}

/* ── Details for the curious ───────────────────────────── */

function Explainer({ plan, params }) {
  const meal = r2((plan.mealPerDay || 0) * 21);
  const rows = [
    { label: 'Base salary', per: plan.base, times: 14, irs: true, ss: true, note: '12 months + June (holiday pay) + December (Christmas pay)' },
    plan.commission ? { label: 'Commission (100% of target)', per: plan.commission, times: 12, irs: true, ss: plan.commissionSS !== false, note: 'not paid in June/December extra' } : null,
    plan.mealPerDay && plan.mealType !== 'none' ? { label: `Lunch allowance (${fmt(plan.mealPerDay)} × 21 days)`, per: meal, times: 11, irs: plan.mealType === 'card' ? plan.mealPerDay > params.mealExempt.card : plan.mealPerDay > params.mealExempt.cash, ss: false, note: 'none in the holiday month; tax-free up to the legal limit' } : null,
    plan.allowance && plan.allowanceType !== 'reimbursement' ? { label: plan.allowanceLabel || 'Allowance', per: plan.allowance, times: 12, irs: !!plan.allowanceTaxable, ss: !!plan.allowanceTaxable, note: '' } : null,
    plan.yearlyBonus ? { label: plan.yearlyBonusLabel || 'Yearly bonus', per: plan.yearlyBonus, times: 1, irs: true, ss: true, note: `paid in ${monthName(plan.yearlyBonusMonth)}` } : null,
  ].filter(Boolean);
  const total = r2(rows.reduce((a, r) => a + r.per * r.times, 0));
  const taxed = r2(rows.filter((r) => r.irs).reduce((a, r) => a + r.per * r.times, 0));
  return (
    <div className="stack">
      <p className="dim">Each part is paid a different number of times a year, so a year isn't just the monthly amount × 12.</p>
      <div className="table-wrap" style={{ border: 0 }}>
        <table className="plain">
          <thead><tr><th>Part</th><th>Each time</th><th>Times a year</th><th>Per year</th><th className="left">IRS?</th><th className="left">Social security?</th><th className="left hide-sm">Note</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td>{r.label}</td><td><Money value={r.per} /></td><td>×{r.times}</td><td><Money value={r.per * r.times} /></td>
                <td className="left">{r.irs ? 'Yes' : 'No'}</td><td className="left">{r.ss ? `Yes (${plan.ssRate}%)` : 'No'}</td><td className="left small muted hide-sm">{r.note}</td>
              </tr>
            ))}
            <tr className="total"><td>Gross per year</td><td /><td /><td><Money value={total} /></td><td colSpan={3} className="left small muted">of which taxed: {fmt(taxed)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="formula">{[
        'Monthly IRS = pay × the rate in the official table − a fixed amount, rounded down. June/December pay is taxed on its own.',
        `IRS Jovem: that rate is only applied to the part that isn't tax-free (tax-free part at most ${fmt(irsJovemCap(params) / 14)} per payment).`,
        `Yearly IRS: income − ${fmt(specificDeductionAmount(params))}, taxed at the rate of the whole income, minus deductions (health, invoices with NIF…).`,
        'Whatever was taken during the year above that comes back as a refund the next year.',
      ].join('\n')}</div>
    </div>
  );
}

function JovemSettings({ person }) {
  const { commit } = useApp();
  const j = person.irsJovem || {};
  const ded = useMoneyText(person.personalDeductions ?? 250, { allowNegative: false });
  const save = (patch, label) => commit(label || `${person.name}: IRS Jovem`, (s) => updatePerson(s, person.id, { irsJovem: { ...j, ...patch } }));
  const years = [];
  for (let y = 2018; y <= 2040; y++) years.push(y);
  return (
    <div className="stack">
      <Toggle checked={!!j.enabled} onChange={(v) => save({ enabled: v, firstYear: j.firstYear || new Date().getFullYear() }, `${person.name}: IRS Jovem ${v ? 'on' : 'off'}`)} label="IRS Jovem (35 or younger, first 10 years of work)" />
      {j.enabled && (
        <div className="grid3">
          <Field label="First year working, on your own IRS">
            <select className="input" value={j.firstYear || ''} onChange={(e) => save({ firstYear: Number(e.target.value) })}>{years.map((y) => <option key={y}>{y}</option>)}</select>
          </Field>
          <Field label="Years that don't count" hint="Still on your parents' IRS, or no work that year" group>
            <div className="row wrap">
              {years.filter((y) => y >= (j.firstYear || 2018) && y < (j.firstYear || 2018) + 12).map((y) => (
                <label key={y} className="chip" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={(j.skipYears || []).includes(y)} onChange={(e) => save({ skipYears: e.target.checked ? [...(j.skipYears || []), y] : (j.skipYears || []).filter((x) => x !== y) })} /> {y}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Your employer applies it every month" hint="You asked HR; otherwise it only comes back in the yearly refund" group>
            <Toggle checked={j.atSource !== false} onChange={(v) => save({ atSource: v })} label={j.atSource !== false ? 'Yes' : 'No'} />
          </Field>
        </div>
      )}
      <div className="row wrap" style={{ alignItems: 'flex-end' }}>
        <div style={{ width: 300 }}><MoneyField label="Deductions in the yearly IRS (guess)" hint="Health, education, invoices with your NIF… 250 € is easy to reach" m={ded} /></div>
        {ded.parsed.ok && ded.parsed.value !== null && ded.parsed.value !== (person.personalDeductions ?? 250) && (
          <button type="button" className="btn small" onClick={() => commit(`${person.name}: IRS deductions ${fmt(ded.parsed.value)}`, (s) => updatePerson(s, person.id, { personalDeductions: ded.parsed.value }))}>Save</button>
        )}
      </div>
    </div>
  );
}

function FullYears({ proj }) {
  return (
    <div className="table-wrap">
      <table className="sheet">
        <thead><tr><th className="left">Year</th><th>Taxed income</th><th>Social security</th><th>IRS taken during the year</th><th>IRS actually due</th><th>Refund</th></tr></thead>
        <tbody>
          {proj.rows.filter((r) => r.annual.gross > 0).map((r) => (
            <tr key={r.year}><td className="left">{r.year}{r.estimatedTax ? <span className="exp">estimate</span> : null}</td><td><Money value={r.annual.gross} /></td><td><Money value={r.annual.ss} /></td><td><Money value={r.annual.irs} /></td><td>{r.settlement ? <Money value={r.settlement.tax} /> : '—'}</td><td>{r.settlement ? <Money value={r.settlement.refund} color /> : '—'}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Pay() {
  const { data, model, commit, me } = useApp();
  const [pid, setPid] = useState(me || data.people[0]?.id);
  const person = data.people.find((p) => p.id === pid) || data.people[0];
  const [dialog, setDialog] = useState(null); // 'edit' | 'raise' | 'about' | 'add' | 'remove'
  const [newName, setNewName] = useState('');
  const [removeMonth, setRemoveMonth] = useState(model.current);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const proj = useMemo(() => (person ? jovemProjection(data, person.id) : { rows: [] }), [data, person]);
  const cmp = useMemo(() => (person ? compareFirstYear(data, person.id) : null), [data, person]);
  if (!person) return null;
  const plan = versionAt(person.plans, model.current) || person.plans[0];
  const employee = plan?.mode === 'employee';
  const params = taxParamsFor(Number(model.current.slice(0, 4)), data.taxYears).params;
  const hasHistory = Object.values(data.months).some((m) => m.people?.[person.id]);

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div><h1>Pay</h1><p>What each of you earns, and what actually reaches the bank.</p></div>
        <span className="spacer" />
        <div className="seg" role="tablist" aria-label="Person">
          {data.people.map((p) => <button key={p.id} type="button" role="tab" aria-selected={p.id === person.id} aria-pressed={p.id === person.id} onClick={() => setPid(p.id)}><span className="pill-person"><PersonDot color={p.color} size={8} />{p.name}{p.until ? ' (left)' : ''}</span></button>)}
        </div>
        <button type="button" className="btn small" onClick={() => setDialog('add')}><Icon name="plus" />Person</button>
      </div>

      <Card
        title={<span className="pill-person"><PersonDot color={person.color} />{person.name}'s salary</span>}
        actions={(
          <>
            <button type="button" className="btn small primary" onClick={() => setDialog('edit')}><Icon name="edit" />Change</button>
            <button type="button" className="btn small" onClick={() => setDialog('raise')}>Got a raise?</button>
            <button type="button" className="btn small ghost" onClick={() => setDialog('about')}>About {person.name}</button>
          </>
        )}
      >
        <SalarySummary person={person} />
        <p className="small muted" style={{ marginTop: 10 }}>Paid on the {paydayRuleLabel(person.payday).toLowerCase()}.</p>
      </Card>

      {employee && <OfferComparison person={person} />}
      {employee && <TakeHome person={person} proj={proj} />}
      {employee && <JovemYears proj={proj} />}

      <details className="card more-card">
        <summary><b>More details</b> <span className="small muted">— where each number comes from, IRS settings, salary history</span></summary>
        <div className="stack" style={{ gap: 22, marginTop: 14 }}>
          {employee && <section><h3 className="detail-title">Where the numbers come from</h3><Explainer plan={withPlanDefaults(plan)} params={params} /></section>}
          {employee && <section><h3 className="detail-title">IRS settings</h3><JovemSettings person={person} /></section>}
          {employee && cmp && cmp.difference > 1 && (
            <section className="banner info">
              <Icon name="info" className="icon" />
              <div className="stack tight">
                <b>What if {cmp.firstYear} didn't count?</b>
                <div>If {person.name} is still on the parents' IRS for {cmp.firstYear} (possible up to age 25, with at most 12 880 € of income that year), the 10 IRS Jovem years start in {cmp.firstYear + 1}. That's about <b>{fmt(cmp.difference)} more</b> over {cmp.years[0]}–{cmp.years[1]}, but {cmp.firstYear}'s income is then taxed in the parents' IRS. Decide with them before telling HR.</div>
              </div>
            </section>
          )}
          {employee && proj.rows.length > 0 && <section><h3 className="detail-title">IRS per year, in full</h3><FullYears proj={proj} /></section>}
          <section>
            <h3 className="detail-title">Salary history</h3>
            {person.plans.map((p, i) => (
              <div className="list-row" key={p.id}>
                <span className="badge upcoming">{i === 0 ? 'First' : `From ${monthLabel(p.from)}`}</span>
                <span style={{ flex: 1, minWidth: 200 }} className="small">{p.mode === 'net' ? `Take-home ${fmt(p.net)}` : p.mode === 'none' ? 'No income' : `Base ${fmt(p.base)}${p.commission ? ` + commission ${fmt(p.commission)}` : ''}`}{i === 0 ? ` · from ${p.startDate ? dateLabel(p.startDate, { weekday: false }) : monthLabel(p.from)}` : ''}</span>
                {person.plans.length > 1 && i > 0 && <button type="button" className="btn ghost small danger" aria-label={`Delete salary from ${monthLabel(p.from)}`} onClick={() => setDeletingPlan(p)}>Delete</button>}
              </div>
            ))}
          </section>
          {data.people.length > 1 && (
            <section><button type="button" className="btn ghost small danger" onClick={() => setDialog('remove')}>Remove {person.name} from the app…</button></section>
          )}
        </div>
      </details>

      {(dialog === 'edit' || dialog === 'raise') && <SalaryDialog person={person} mode={dialog} onClose={() => setDialog(null)} />}
      {dialog === 'about' && <PersonDialog person={person} onClose={() => setDialog(null)} />}
      {dialog === 'add' && (
        <ConfirmDialog title="Add a person" confirmLabel="Add" onCancel={() => setDialog(null)} onConfirm={() => {
          const r = commit(`Add ${newName}`, (s) => addPerson(s, { name: newName }));
          if (!r.error) { setDialog(null); setNewName(''); }
        }}>
          <Field label="Name"><input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus maxLength={60} /></Field>
        </ConfirmDialog>
      )}
      {dialog === 'remove' && (
        <ConfirmDialog title={`Remove ${person.name}?`} danger confirmLabel="Remove" onCancel={() => setDialog(null)} onConfirm={() => {
          const r = commit(`Remove ${person.name}`, (s) => removePerson(s, person.id, hasHistory ? removeMonth : undefined), { undoToast: true });
          if (!r.error) { setDialog(null); setPid(data.people.find((p) => p.id !== person.id)?.id); }
        }}>
          {hasHistory ? (
            <>
              <p className="dim">Their past months stay; they stop taking part after the month below.</p>
              <Field label="Last month they take part" group><MonthSelect ariaLabel="Last month they take part" value={removeMonth} onChange={(v) => v && setRemoveMonth(v)} /></Field>
            </>
          ) : <p className="dim">Nothing has been recorded for {person.name} yet, so they're removed completely.</p>}
        </ConfirmDialog>
      )}
      {deletingPlan && (
        <ConfirmDialog title={`Delete the salary from ${monthLabel(deletingPlan.from)}?`} message="Those months go back to the salary before it. Confirmed paydays don't change." danger confirmLabel="Delete"
          onCancel={() => setDeletingPlan(null)} onConfirm={() => { commit(`${person.name}: delete salary from ${monthLabel(deletingPlan.from)}`, (s) => deletePlan(s, person.id, deletingPlan.id), { undoToast: true }); setDeletingPlan(null); }} />
      )}
    </div>
  );
}
